import {Concept} from 'types';
import type {WorkSummary} from 'api/types';
import type {
  Part,
  PartSchema,
  TypedSchema,
  TypedSchemaModifier,
} from 'schemaDefinition/types';
import {Separator, Today} from 'schemaDefinition/types';
import type {Agent, AgentPerson} from 'services/agent/types';
import type {MainTitle} from 'services/metadata/types';
import type {Schemas} from '../../api/dto.generated';
import {CodeListRef} from '../references';
import {mainTitlesPart} from './manifestationSchema';
import {agentBAREParts} from './parts';
import {workDataSchema} from './workSchema';

export type AgentPersonWithWorks = AgentPerson &
  WorkSummary &
  Pick<Schemas.AgentWorkRoleDto, 'roles'> & {
    mainTitles?: MainTitle[];
  };

const agentPrizesSchema: PartSchema<AgentPersonWithWorks> = {
  type: 'schema',
  name: 'prizes',
  parts: [
    [
      {
        name: 'year',
        type: 'year',
      },
      {
        name: 'type',
        type: 'codelist',
        codelistId: 'PRIZE_TYPE',
      },
      {
        type: 'linkedLiterary',
        name: 'prize',
        roleCodelistId: 'work.linkedRole',
        cardinality: 'single',
        linkRole: 'awarded.prize',
      },
      {
        type: 'expand',
        role: 'work-link',
        when: [
          {
            condition: [
              {
                $includes: {
                  arg1: {$ref: ':type'},
                  arg2: [
                    CodeListRef.PRIZE_TYPE.Author,
                    CodeListRef.PRIZE_TYPE.Illustrator,
                    CodeListRef.PRIZE_TYPE.Narrator,
                    CodeListRef.PRIZE_TYPE.Translator,
                  ],
                },
                default: true,
              },
            ],
            parts: [
              {
                type: 'linkedLiterary',
                name: 'work',
                // key: 'winner.work',
                cardinality: 'single',
                roleCodelistId: 'prize.linkedRole',
                linkRole: 'awardedFor.work',
              },
            ],
          },
        ],
      },
    ],
  ],
  cardinality: 'multiple',
  readonly: true,
  noValidation: true,
};

const agentWorksSchema: PartSchema<AgentPersonWithWorks> = {
  type: 'schema',
  name: 'works',
  cardinality: 'multiple',
  noValidation: true,
  variant: 'table',
  parts: [
    ...(workDataSchema.parts as Part<AgentPersonWithWorks>[]),
    mainTitlesPart as PartSchema<AgentPersonWithWorks>,
    {
      type: 'thesaurus',
      name: 'themaLabels',
      key: 'themaLabels',
      cardinality: 'multiple',
      thesaurusId: 'thema',
      showCode: false,
    },
    {
      type: 'text',
      name: 'themaCodes',
      key: 'themaCodes',
      cardinality: 'multiple',
    },
    {
      type: 'codelist',
      name: 'roles',
      cardinality: 'multiple',
      codelistId: 'WORK_AND_EXPRESSION_ROLE_TYPE',
    },
  ],
};

export const agentPersonSchema: TypedSchema<AgentPersonWithWorks> = {
  name: 'person',
  key: 'person',
  parts: [
    {
      type: 'nameVariant',
      agentType: Concept.person,
      name: 'nameVariants',
      cardinality: 'multiple',
      required: true,
    },
    Separator('card', 'line'),
    [
      ...agentBAREParts,
      {
        type: 'bool',
        name: 'pseudonym',
      },
    ],
    {
      type: 'text',
      name: 'id',
      readonly: true,
    },
    {
      type: 'expand',
      role: 'personLinks',
      when: [
        {
          condition: {
            arg: {$ref: '.pseudonym'},
          },
          parts: [
            {
              type: 'linkedLiterary',
              name: 'links',
              key: 'links',
              cardinality: 'multiple',
              roleCodelistId: 'pseudonym.linkedRole',
            },
          ],
        },
      ],
      default: [
        {
          type: 'linkedLiterary',
          name: 'links',
          key: 'links',
          cardinality: 'multiple',
          roleCodelistId: 'realPerson.linkedRole',
        },
      ],
    },
    Separator('line', 'none'),
    [
      {
        type: 'yearOrDate',
        name: 'birth',
        includeBce: true,
        includeApproximate: true,
        validation: [
          {
            max: Today,
            messageKey: 'birthLatestToday',
          },
          {
            max: {$ref: ':death'},
            messageKey: 'birthBeforeDeath',
          },
        ],
      },
      {
        type: 'yearOrDate',
        name: 'death',
        includeBce: true,
        includeApproximate: true,
        validation: {
          min: {$ref: ':birth'},
          max: Today,
          messageKey: 'deathAfterBirth',
        },
      },
    ],
    [
      {
        type: 'codelist',
        name: 'countryOfBirth',
        codelistId: 'COUNTRY',
      },
      {
        type: 'codelist',
        name: 'country',
        key: 'countryFlourishedIn',
        codelistId: 'COUNTRY',
      },
    ],
    [
      {
        type: 'text',
        name: 'profession',
      },
      {
        type: 'text',
        name: 'occupationalField',
        cardinality: 'multiple',
      },
    ],

    {
      type: 'codelist',
      name: 'gender',
      codelistId: 'GENDER',
      required: true,
      variant: 'buttons',
    },
    Separator('line', 'line'),
    {
      type: 'textarea',
      name: 'externalComments',
      maxLength: 5000,
    },
    {
      ...Separator('card', 'none'),
      role: 'internalComments',
      titleKey: 'internalComments',
    },
    {
      type: 'textarea',
      name: 'internalComments',
      maxLength: 5000,
    },
    Separator('card', 'card'),
    agentPrizesSchema,

    Separator('card', 'card'),
    agentWorksSchema,
  ],
};

/**
 * Dont allow external comment to be changed when national id is set,
 * because we dont update (only on create) this field in BARE anyways.
 */
export const nationIdSchemaModifier: TypedSchemaModifier<Agent> = [
  {
    name: 'externalComments',
    readonly: true,
  },
];
